import React, { useRef }                from 'react';
import {
    Link,
    useParams }             from 'react-router-dom';

import * as AgencyType      from 'shared/types/Agency';
import * as UserType        from 'shared/types/User';
import * as VehicleType     from 'shared/types/Vehicle';
import * as hhmmUtil        from 'shared/utils/hhmm';
import englishWeekdays      from 'shared/utils/englishWeekdays';
import dayjs                from 'shared/utils/day-timezone';
import hashById             from 'shared/utils/hashById';

import tectransit           from 'utils/TecTransit';
import getApiPromise        from 'utils/getApiPromise';
import * as MenuItem        from 'components/MenuItem';

interface Props {
}

const lowEnglishWeekdays = englishWeekdays.map( wd => {
    return wd.toLowerCase();
});
const hhmmToMins = ( hhmm:string ) : number => {
    const parts = (hhmm||'0:0').split(':');
    return Number(parts[0])*60+Number(parts[1]);
}
const minsToHhmm = ( mins:number ) :string => {
    const hours = String(Math.floor(mins/60));
    const mins_ = String(mins%60);
    return `${hours.padStart(2,"0")}:${mins_.padStart(2,"0")}`;
}
const getStrScheduleByDriverIdFromSchedule = ( scheduleByDriverId:Record<number,AgencyType.DriverSchedule> ) : Record<string,Record<string,string>> => {
    const rangeToString = ( range:[number,number,number] ) => {
        return `from ${hhmmUtil.getHHMMFromMins(range[0])} to ${hhmmUtil.getHHMMFromMins(range[1])} vehicle #${range[2]}`;
    }
    return Object.entries(scheduleByDriverId).reduce((acc,[driverId,driverSchedule]) => {
        acc[driverId] = lowEnglishWeekdays.reduce( (acc,weekday) => {
            acc[weekday] = (driverSchedule[weekday]||[]).map(rangeToString).join('\n');
            return acc;
        },{} as Record<string,string>);
        return acc;
    },{} as Record<string,Record<string,string>>);
}
const getScheduleByDriverIdFromStrSchedule = (
    strScheduleByDriverId   :   Record<string,Record<string,string>>,
    vehiclesById            :   Record<string,VehicleType.Vehicle>,
    driversById             :   Record<string,UserType.User>
 ) => {
    const stringToRange = ( s:string ) : [number,number,number] => {
        // @ts-ignore
        const matches = s.match(/^\s*(?:from|f)?\s*(?<from>(\d{1,2}):(\d{1,2}))\s*(?:to|t|-)?\s*(?<to>(\d{1,2}):(\d{1,2}))\s*(?:vehicle|v)?\s*#?(?<vehicle>\d+)\s*$/);
        if( !matches )
            throw Error(`'${s}' is not in format 'from hh:mm to hh:mm vehicle #'`);
        const from  = hhmmToMins(matches.groups!['from']);
        if( from<0 || from>(24*60) )
            throw Error(`in '${s}' from '${matches.groups!['from']}' does not signify minutes in a day`);
        const to    = hhmmToMins(matches.groups!['to']);
        if( to<0 || to>(24*60) )
            throw Error(`in '${s}' to '${matches.groups!['to']}' does not signify minutes in a day`);
        if( from>=to )
            throw Error(`in '${s}' '${matches.groups!['from']}' should be less than '${matches.groups!['to']}'`);
        const vid   = Number(matches.groups!['vehicle']);
        if( !vehiclesById[vid] )
            throw Error(`in '${s}' '${matches.groups!['vehicle']}' does not signify a vehicle id'`);
        return [from,to,vid];
    }
    const validateScheduleByDriverId = ( scheduleByDriverId:Record<string,AgencyType.DriverSchedule> ) => {
        // Make sure that no driver is allocated to 2 different vehicles at the same time
        AgencyType.rehashSchedule(
            scheduleByDriverId,
            (driverId,vehicleId)=>driverId,
            (driverId,vehicleId)=>vehicleId,
            (firstNdx:number,newRange:[number,number,any],existingRange:[number,number,any],overlap:[number,number]) => {
                throw Error(`For driver '${driversById[firstNdx]?.name||`#${firstNdx}`}' there is an overlap in ranges between [${minsToHhmm(overlap[0])},${minsToHhmm(overlap[1])}] for vehicles #${existingRange[2]} and #${newRange[2]}`);
            }
        );
        // Make sure that no vehicle is allocated to 2 different drivers at the same time
        AgencyType.rehashSchedule(
            scheduleByDriverId,
            (driverId,vehicleId)=>vehicleId,
            (driverId,vehicleId)=>driverId,
            (firstNdx:number,newRange:[number,number,any],existingRange:[number,number,any],overlap:[number,number]) => {
                throw Error(`For vehicle #${firstNdx} there is an overlap in ranges between [${minsToHhmm(overlap[0])},${minsToHhmm(overlap[1])}] for drivers '${driversById[existingRange[2]]?.name||`#${existingRange[2]}`}' and '${driversById[newRange[2]]?.name||`#${newRange[2]}`}'`);
            }
        );
        return scheduleByDriverId;
    }
    return validateScheduleByDriverId(
        Object.entries(strScheduleByDriverId).reduce((acc,[driverId,strDriverSchedule]) => {
             const driverSchedule = Object.entries(strDriverSchedule as Record<string,string>).reduce((acc,[weekday,schedule]) => {
                try {
                    const ranges = schedule.split('\n').map(v=>v.trim()).filter(v=>!!v).map(stringToRange);
                    if( ranges.length>0 )
                        acc[weekday] = ranges;
                }
                catch( err) {
                    throw Error(`Schedule of driver '${driversById[Number(driverId)]?.name}' for ${weekday} is invalid (${(err as Error).message})`);
                }
                return acc;
            },{} as AgencyType.DriverSchedule);
            if( Object.keys(driverSchedule).length>0 )
                acc[Number(driverId)] = driverSchedule;
            return acc;
        },{} as Record<number,AgencyType.DriverSchedule>)
    );
}
export const DriverSchedule : React.FC<Props> = props => {
    const params                            = useParams<{byType?:string,typeId?:string}>();
    const formRef                           = useRef<HTMLFormElement>(null);

    const [byType,setByType]                = React.useState<string>(params.byType||'byDriver');
    const [driverId,setDriverId]            = React.useState<string>(params.typeId||"0");
    const [vehicleId,setVehicleId]          = React.useState<string>(params.typeId||"0");
    const [weekdayName,setWeekdayName]      = React.useState<string>(params.typeId||"0");
    const [driversById,setDriversById]      = React.useState<Record<number,UserType.User>>();
    const [vehiclesById,setVehiclesById]    = React.useState<Record<number,VehicleType.Vehicle>>();
    const [driversRefreshCount,
            setDriversRefreshCount]          = React.useState<number>(0);
    // This is what we are building
    const [scheduleByDriverId,
           setScheduleByDriverId]           = React.useState<Record<number,AgencyType.DriverSchedule>>(tectransit.agency.schedule_by_driver_id);
    // For the purposes of the component state the schedule is represented by strings
    // instead of [number,number,number][] ranges. The convertion to ranges happens
    // at the last moment
    const [strScheduleByDriverId,
           setStrScheduleByDriverId]        = React.useState<Record<string,Record<string,string>>>(getStrScheduleByDriverIdFromSchedule(scheduleByDriverId));

    React.useEffect(() => {
        console.log(`Loading drivers`,{driversRefreshCount});
        getApiPromise<UserType.Dehydrated[]>('/api/dispatcher/users','GET',undefined, { inRoles : 'Driver' }).then( dehydrated => {
            if( !dehydrated || dehydrated.err )
                throw Error(`Cannot get drivers (${dehydrated?.err||'unknown error'})`);
            return hashById(dehydrated.map(UserType.hydrate));
        }).then( driversById => {
            // This could be helpful
            if( tectransit.user._id in driversById )
                tectransit.user = driversById[tectransit.user._id];
            setDriversById(driversById);
        });
    },[driversRefreshCount]);

    React.useEffect(() => {
        console.log(`Loading vehicles`);
        getApiPromise<VehicleType.Vehicle[]>('/api/dispatcher/vehicles','GET',undefined).then( vehicles => {
            if( !vehicles || vehicles.err )
                throw Error(`Cannot get users (${vehicles?.err||'unknown error'})`);
            return hashById(vehicles);
        }).then( vehiclesById => {
            setVehiclesById(vehiclesById);
        });
    },[]);

    if( !driversById )
        return (
            <MenuItem.MenuItem title={`Drivers Schedule`}>
                Loading Drivers
            </MenuItem.MenuItem>
        );
    if( !vehiclesById )
        return (
            <MenuItem.MenuItem title={`Drivers Schedule`}>
                Loading Vehicles
            </MenuItem.MenuItem>
        );

    const getDriverStrScheduleFromForm = ( form:HTMLFormElement ) : Record<string,string> => {
        return lowEnglishWeekdays.reduce((acc,weekday) => {
            acc[weekday] = form[weekday]?.value.trim()||'';
            return acc;
        },{} as Record<string,string>);
    }
    const getWeekdayVehicleSchedule = ( vid:number, lowWeekday:string ) => {
        return Object.entries(scheduleByDriverId).reduce( (acc,[driverId,driverSchedule]) => {
            const driverWeekdayVehicleSchdule = (driverSchedule[lowWeekday]||[]).filter(r=>(r[2]===vid)).map( r => {
                return [r[0],r[1],Number(driverId)] as [number,number,number];
            });
            acc.push(...driverWeekdayVehicleSchdule);
            return acc;
        },[] as [number,number,number][]);
    }
    const getDriverSelect = ( driverId:string ) => {
        const driver = driversById[Number(driverId)];
        if( !driver ) {
            return (<div>
                Driver #{driverId} is not known
            </div>);
        }
        return (
            <select
                className   = "input-theme input-theme-min"
                value       = {driver._id}
                onChange    = {(e) => {
                    const form = formRef.current;
                    if( !form )
                        return;
                    // Save the schedule for chosen driver
                    setStrScheduleByDriverId({
                        ...strScheduleByDriverId,
                        [driver._id] : getDriverStrScheduleFromForm(form)
                    });
                    // And choose a new driver
                    setDriverId(e.target.value.trim());
                }}
            >
                {Object.values(driversById||{}).map( d => {
                    const days = Object.entries(strScheduleByDriverId[d._id]||{})
                        .filter( ([wd,str]) => {
                            return str.length>0;
                        })
                        .map( ([wd,ranges]) => {
                            return wd.substring(0,1).toUpperCase();
                        });
                    return <option key={d._id} value={d._id}>{d.name} ({days.length?days.join(','):'no schedule'})</option>
                })}
            </select>
        );
    }
    const getVehicleSelect = ( vehicleId:string ) => {
        const vehicle = vehiclesById[Number(vehicleId)];
        if( !vehicle ) {
            return (<div>
                Vehicle #{vehicleId} is not known
            </div>);
        }
        const driverSchedules = Object.values(scheduleByDriverId);
        return (
            <select
                className   = "input-theme input-theme-min"
                value       = {vehicle._id}
                onChange    = {(e) => {
                    setVehicleId(e.target.value.trim());
                }}
            >
                {Object.values(vehiclesById||{}).map( v => {
                    const days = lowEnglishWeekdays.filter( weekday => {
                            // Should return true for any day that occurs in any driver schedule for this vehicle
                            return driverSchedules.some(ds=>(ds[weekday]?.some(r=>(r[2]===v._id))));
                        })
                        .map( wd => {
                            return wd.substring(0,1).toUpperCase();
                        });
                    return <option key={v._id} value={v._id}>Vehicle #{v._id} ({days.length?days.join(','):'no schedule'})</option>
                })}
            </select>
        );
    }
    const getWeekdayNameSelect = ( weekdayName:string ) => {
        return (
            <select
                className    = "input-theme input-theme-min"
                defaultValue = {driverId}
                onChange     = {(e) => {
                    setWeekdayName(e.target.value.trim());
                }}
            >
                {englishWeekdays.map( wd => {
                    return (
                        <option key={wd} value={wd}>
                            {wd}
                        </option>
                    );
                })}
            </select>
        );
    }
    const getDriverTable = ( driverId:string ) => {
        const driver = driversById[Number(driverId)];
        if( !driver ) {
            return (<div>
                Driver #{driverId} is not known
            </div>);
        }
        const strDriverSchedule = strScheduleByDriverId[Number(driverId)]||{};
        return (
            <table>
                <tbody>
                    <tr>
                        <td colSpan={2}>
                            Driver <Link to={`/Manager/Passenger/${driver._id}`}>{driver.name}</Link> has his schedule last updated at{" "}
                            <strong>{driver.schedule_changed_at ? dayjs(driver.schedule_changed_at).tz(tectransit.agency.time_zone).format(tectransit.timeFormat) : "n/a"}</strong>.{" "}
                            <br/>
                            {((driver.schedule_changed_at||0)>(driver.schedule_committed_at||0)) ? (<>
                                It has <strong style={{color:'red'}}>NOT YET</strong> committed to this schedule.
                            </>) : (<>
                                It has last committed to the schedule at{" "}
                                <strong>{driver.schedule_committed_at ? dayjs(driver.schedule_committed_at).tz(tectransit.agency.time_zone).format(tectransit.timeFormat) : "n/a"}</strong>
                            </>)}
                        </td>
                    </tr>
                    {lowEnglishWeekdays.map( weekday => {
                        return (
                            <tr key={`driver_${driver._id}_${weekday}`}>
                                <td valign='top'>
                                    <strong>{weekday.substring(0,1).toUpperCase()}{weekday.substring(1)}</strong>
                                </td>
                                <td>
                                    <textarea
                                        name            = {weekday}
                                        rows            = {5}
                                        cols            = {50}
                                        style           = {{resize:'none'}}
                                        defaultValue    = {strDriverSchedule[weekday]}
                                    />
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        );
    }
    const getVehicleTable = ( vehicleId:string ) => {
        const vehicle = vehiclesById[Number(vehicleId)];
        if( !vehicle ) {
            return (<div>
                Vehicle #{vehicleId} is not known
            </div>);
        }
        return (
            <table>
                <tbody>
                    {lowEnglishWeekdays.map( weekday => {
                        return (
                            <tr key={`vehicle_${vehicle._id}_${weekday}`}>
                                <td valign='top'>
                                    <strong>{weekday.substring(0,1).toUpperCase()}{weekday.substring(1)}</strong>
                                </td>
                                <td>
                                    <table>
                                        <tbody>
                                        {getWeekdayVehicleSchedule(vehicle._id,weekday).map( (range,rndx) => {
                                            return (
                                                <tr key={rndx}><td>
                                                    from <strong>{minsToHhmm(range[0])}</strong> to{" "}
                                                    <strong>{minsToHhmm(range[1])}</strong>{" "}
                                                    driver <Link to={`/Manager/User/${range[2]}`}>{driversById[range[2]]?.name||`driver #${range[2]}`}</Link>
                                                </td></tr>
                                            );
                                        })}
                                        </tbody>
                                    </table>
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        )
    }
    const getWeekdayVehiclesTable = ( weekdayName:string ) => {
        if( !englishWeekdays.includes(weekdayName) ) {
            return (
                <div>
                    Weekday #{weekdayName} is not known
                </div>
            );
        }
        const lowWeekdayName = weekdayName.toLowerCase();
        return (
            <table>
                <tbody>
                    {Object.values(vehiclesById).map( v => {
                        return (
                            <tr key={v._id}>
                                <td valign='top'>
                                    <strong>Vehicle #{v._id}</strong>
                                </td>
                                <td>
                                    <table>
                                        <tbody>
                                            {getWeekdayVehicleSchedule(v._id,lowWeekdayName).map( (range,rndx) => {
                                                return (
                                                    <tr key={rndx}>
                                                        <td>
                                                            from <strong>{minsToHhmm(range[0])}</strong>{" "}
                                                            to <strong>{minsToHhmm(range[1])}</strong>{" "}
                                                            driver <Link to={`/Manager/User/%${range[2]}`}>{driversById[range[2]]?.name||`driver #${range[2]}`}</Link>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        );
    }
    const getWeekdayDriversTable = ( weekdayName:string ) => {
        if( !englishWeekdays.includes(weekdayName) ) {
            return (
                <div>
                    Weekday #{weekdayName} is not known
                </div>
            );
        }
        const lowWeekdayName = weekdayName.toLowerCase();
        return (
            <table>
                <tbody>
                    {Object.values(driversById).map( d => {
                        const driverSchedule = (scheduleByDriverId[d._id]||{})[lowWeekdayName]||[];
                        return (
                            <tr key={d._id}>
                                <td valign='top'>
                                    <Link to={`/Manager/User/${d._id}`}>{d.name}</Link>
                                </td>
                                <td>
                                    <table>
                                        <tbody>
                                            {driverSchedule.map( (range,rndx) => {
                                                return (
                                                    <tr key={rndx}>
                                                        <td>
                                                            from <strong>{minsToHhmm(range[0])}</strong>{" "}
                                                            to <strong>{minsToHhmm(range[1])}</strong>{" "}
                                                            vehicle #<strong>{range[2]}</strong>
                                                        </td>
                                                    </tr>
                                                )
                                            })}
                                        </tbody>
                                    </table>
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        );
    }
    return MenuItem.withMenuItem("Driver Schedule", (alert) => {
        const [
            finalId,
            secondRow,
            thirdRow
        ] = (byType==='byVehicle') ? (( id:string ) => {
                const finalVehicleId = vehiclesById[Number(id)] ? id : Object.keys(vehiclesById)[0];
                return [
                    finalVehicleId,
                    getVehicleSelect(finalVehicleId),
                    getVehicleTable(finalVehicleId)
                ];
            })(vehicleId!) : (byType==='byWeekdayVehicles') ? (( id:string ) => {
                const finalWeekdayName = englishWeekdays.includes(id) ? id : englishWeekdays[0];
                return [
                    finalWeekdayName,
                    getWeekdayNameSelect(finalWeekdayName),
                    getWeekdayVehiclesTable(finalWeekdayName)
                ];
            })(weekdayName!) : (byType==='byWeekdayDrivers') ? (( id:string ) => {
                const finalWeekdayName = englishWeekdays.includes(id) ? id : englishWeekdays[0];
                return [
                    finalWeekdayName,
                    getWeekdayNameSelect(finalWeekdayName),
                    getWeekdayDriversTable(finalWeekdayName)
                ];
            })(weekdayName!) : (( id:string ) => {
                const finalDriverId = driversById[Number(id)] ? id : Object.keys(driversById)[0];
                return [
                    finalDriverId,
                    getDriverSelect(finalDriverId),
                    getDriverTable(finalDriverId)
                ];
            })(driverId!);
        console.log({
            finalId,
            byType
        });
        return (
            <div className="wrapper">
                <form ref={formRef}>
                    <table>
                        <tbody>
                            <tr>
                                <td>
                                    <select
                                        className    = "input-theme input-theme-min"
                                        defaultValue = {byType}
                                        onChange     = {(e) => {
                                            // We update `scheduleByDriverId` every time when the type of this component is changed
                                            try {
                                                setScheduleByDriverId(getScheduleByDriverIdFromStrSchedule(
                                                    strScheduleByDriverId,
                                                    vehiclesById,
                                                    driversById
                                                ));
                                                setByType(e.target.value);
                                            }
                                            catch( err ) {
                                                alert.set(`Cannot save schedule (${(err as Error).message})`);
                                            }
                                        }}
                                    >
                                        {Object.entries({
                                            "byDriver"          : "By Driver",
                                            "byVehicle"         : "By Vehicle",
                                            "byWeekdayVehicles" : "By Weekday Vehicles",
                                            "byWeekdayDrivers"  : "By Weekday Drivers"
                                        }).map( ([value,text]) => {
                                            return <option key={value} value={value}>{text}</option>;
                                        })}
                                    </select>
                                </td>
                            </tr>
                            <tr>
                                <td>
                                    {secondRow}
                                </td>
                            </tr>
                            <tr>
                                <td>
                                    {thirdRow}
                                </td>
                            </tr>
                        </tbody>
                    </table>
                    <button
                        type        = "button"
                        className   = "btn-theme btn-small"
                        onClick     = {(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            const form = formRef.current;
                            if( !form )
                                return;
                            try {
                                const newStrScheduleByDriverId = (byType==='byDriver') ? {
                                    ...strScheduleByDriverId,
                                    [finalId] : getDriverStrScheduleFromForm(form)
                                } : strScheduleByDriverId;
                                const newScheduleByDriverId = getScheduleByDriverIdFromStrSchedule(newStrScheduleByDriverId,vehiclesById,driversById);
                                // Let's parse the new schedule
                                const patch = {
                                    schedule_by_driver_id : newScheduleByDriverId
                                };
                                setStrScheduleByDriverId(newStrScheduleByDriverId);
                                setScheduleByDriverId(newScheduleByDriverId);
                                return getApiPromise('/api/manager/agency','PUT',patch)
                                    .then(res => {
                                        if (!res || res.err)
                                            throw Error(res?.err || `unknown error`);
                                        // Update the agency itself
                                        tectransit.agency = patch;
                                        // Have to refresh the drvivers to get when their schedule was last changed
                                        // It is quite a complicated procedure done in by server in PUT /api/manager/agency
                                        // So the simplest way of learning that is to refresh the drivers from the server
                                        setDriversRefreshCount(driversRefreshCount+1);
                                        alert.set(`Driver schedule is saved`, 5000);
                                    })
                                    .catch(err => {
                                        alert.set(`Cannot save agency (${err.message})`);
                                    });
                            }
                            catch( err ) {
                                alert.set(`Cannot save schedule (${(err as Error).message})`);
                            }
                        }}
                    >
                        Save Driver Schedule
                    </button>
                </form>
            </div>
        );
    });
}

export default DriverSchedule;
